import moment from "moment/moment";
import { Link } from "react-router-dom";
import { IconCheck, IconHome } from "@tabler/icons-react";
import { Table } from "../UI/Table";
import Tooltip from "../UI/Tooltip";
import Card from "../UI/Card";
import Avatar from "../UI/Avatar";
import Status from "../UI/Status";
import { Selectable } from "../UI/Selectable";
import Text, { type Color } from "../UI/Text";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { useAllTenantsBalances } from "../../hooks/use-balances";
// @ts-expect-error pure js
import useBalancesDashboard from "../../hooks/use-balances-dashboard";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import RevisionTag from "./RevisionTag";
import useTableSort from "../../hooks/use-table-sort";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import style from "./LoyersShow.module.css";

const MONTHS_SHOWN = 4;

interface BalanceSummary {
  dg_credit?: number;
  dg_debit?: number;
  balance?: number;
}

interface PaymentEntry {
  date_transac: string;
  montant: number;
}

interface DashboardBalance {
  last_payments?: PaymentEntry[];
}

interface MonthCell {
  label: string;
  montant: number;
  date: string;
}

interface LoyerRow {
  tenant: Tenant;
  property?: Property;
  initials: string;
  dgAJour: boolean;
  dgRecu: number;
  dgAppele: number;
  balance: number;
  soldeColor: Color;
  months: MonthCell[];
}

interface LoyersShowProps {
  tenants: Tenant[];
}

type SortKey = "nom" | "dg" | "solde";

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getInitials(nom?: string): string {
  if (!nom) return "?";
  const parts = nom.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

function DgBadge({ row }: { row: LoyerRow }) {
  return (
    <Tooltip
      content={`Perçu ${currencyFormatter(
        row.dgRecu,
      )} / appelé ${currencyFormatter(row.dgAppele)}`}
    >
      <Status
        color={row.dgAJour ? "success" : "danger"}
        text={row.dgAJour ? "À jour" : "Impayé"}
      />
    </Tooltip>
  );
}

function MonthValue({ month }: { month: MonthCell }) {
  if (month.montant === 0) {
    return (
      <Text as="span" variant="deci" color="muted">
        —
      </Text>
    );
  }
  return (
    <>
      <Text as="span" variant="deci" weight="bold">
        {currencyFormatter(month.montant)}
      </Text>
      <Text as="span" variant="deci" color="muted">
        {month.date}
      </Text>
    </>
  );
}

export default function LoyersShow({ tenants }: LoyersShowProps) {
  const { groupedBalances } = useAllTenantsBalances(false, {
    groupBy: { attr: "id_tenant", unique: true },
  }) as { groupedBalances: Record<number, BalanceSummary> };
  const { groupedBalancesDashboard } = useBalancesDashboard({
    groupBy: { attr: "id_tenant", unique: true },
  }) as { groupedBalancesDashboard: Record<number, DashboardBalance> };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };

  const monthLabels = Array.from({ length: MONTHS_SHOWN }, (_, i) =>
    capitalize(moment().subtract(i, "month").format("MMM")),
  );

  const rows: LoyerRow[] = tenants.map((tenant) => {
    const balanceTenant = groupedBalances[tenant.id];
    const balanceDashboard = groupedBalancesDashboard[tenant.id];

    const dgRecu = balanceTenant?.dg_debit ?? 0;
    const dgAppele = -(balanceTenant?.dg_credit ?? 0);
    const dgSum =
      (balanceTenant?.dg_credit ?? 0) + (balanceTenant?.dg_debit ?? 0);

    const balance = balanceTenant?.balance ?? 0;
    const loyerTotal = (tenant.loyer_hc ?? 0) + (tenant.charges ?? 0);
    let soldeColor: Color;
    if (balance >= 0) {
      soldeColor = "success";
    } else if (balance < -loyerTotal) {
      soldeColor = "danger";
    } else {
      soldeColor = "warning";
    }

    const months: MonthCell[] = monthLabels.map((label, monthIdx) => {
      let montant = 0;
      let date = "";
      balanceDashboard?.last_payments
        ?.filter((payment) =>
          moment()
            .subtract(monthIdx, "month")
            .isSame(moment(payment.date_transac), "month"),
        )
        .forEach((payment) => {
          montant += payment.montant;
          date = moment(payment.date_transac).format("DD/MM/YYYY");
        });
      return { label, montant, date };
    });

    return {
      tenant,
      property: propertiesById[tenant.id_bien],
      initials: getInitials(tenant.nom),
      dgAJour: dgSum >= 0,
      dgRecu,
      dgAppele,
      balance,
      soldeColor,
      months,
    };
  });

  const { sort, toggleSort, directionFor } =
    useTableSort<SortKey>("loyersShow_sort");

  const displayedRows = (() => {
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === "dg")
        return dir * (Number(a.dgAJour) - Number(b.dgAJour));
      if (sort.key === "solde") return dir * (a.balance - b.balance);
      return (
        dir *
        (a.tenant.nom ?? "").localeCompare(b.tenant.nom ?? "", "fr", {
          sensitivity: "base",
        })
      );
    });
  })();

  const isEmpty = rows.length === 0;

  return (
    <>
      {/* ---------- Desktop : tableau ---------- */}
      <div className={style.tableView}>
        <Table variant="card">
          <Table.Header>
            <Table.Column sortDirection={directionFor("nom")}>
              <div className={style.selectAllHeader}>
                <label className={style.checkboxLabel}>
                  <Selectable.Checkbox selectAll className={style.checkbox} />
                  <span className="srOnly">Tout sélectionner</span>
                </label>
                <Table.SortButton
                  direction={directionFor("nom")}
                  onSort={() => toggleSort("nom")}
                >
                  <Text as="span" weight="medium">
                    Locataire
                  </Text>
                </Table.SortButton>
              </div>
            </Table.Column>
            <Table.Column
              sortDirection={directionFor("dg")}
              onSort={() => toggleSort("dg")}
            >
              <Text as="span" weight="medium">
                Dépôt de garantie
              </Text>
            </Table.Column>
            <Table.Column
              align="right"
              sortDirection={directionFor("solde")}
              onSort={() => toggleSort("solde")}
            >
              <div>
                <Text as="div" weight="medium">
                  Solde
                </Text>
                <Text as="div" variant="deci">
                  Actuel
                </Text>
              </div>
            </Table.Column>
            {monthLabels.map((label) => (
              <Table.Column key={label} align="right">
                <div>
                  <Text as="div" weight="medium">
                    {label}
                  </Text>
                  <Text as="div" variant="deci">
                    Montant perçu
                  </Text>
                </div>
              </Table.Column>
            ))}
          </Table.Header>
          <Table.Body>
            {isEmpty ? (
              <Table.Row>
                <Table.Cell colSpan={3 + MONTHS_SHOWN}>
                  <Text className={style.emptyState} color="muted">
                    Aucun locataire
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              displayedRows.map((row, i) => (
                <Table.Row key={row.tenant.id} className={style.row}>
                  <Table.Cell>
                    <div className={style.tenantCell}>
                      <Selectable.Checkbox
                        value={row.tenant.id}
                        index={i}
                        className={style.checkbox}
                      />
                      <div className={style.tenantInfo}>
                        <div className={style.nameRow}>
                          <Link
                            to={`/bailleur/tenants/${row.tenant.id}`}
                            className={style.tenantName}
                          >
                            <Text as="span" weight="bold">
                              {row.tenant.nom}
                            </Text>
                          </Link>
                          <RevisionTag tenant={row.tenant} />
                        </div>
                        {row.property && (
                          <Link
                            to={`/bailleur/properties/${row.property.id}`}
                            className={style.propertyLink}
                          >
                            <IconHome size={14} stroke={1.5} />
                            <Text as="span" variant="deci">
                              {row.property.nom}
                            </Text>
                          </Link>
                        )}
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <DgBadge row={row} />
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Text as="span" weight="bold" color={row.soldeColor}>
                      {currencyFormatter(row.balance)}
                    </Text>
                  </Table.Cell>
                  {row.months.map((month, monthIdx) => (
                    <Table.Cell key={monthIdx} align="right">
                      <div className={style.monthCell}>
                        <MonthValue month={month} />
                      </div>
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </div>

      {/* ---------- Mobile : cartes ---------- */}
      <div className={style.cardView}>
        {isEmpty ? (
          <Card>
            <Text className={style.emptyState} color="muted">
              Aucun locataire
            </Text>
          </Card>
        ) : (
          <>
            <label className={style.mobileSelectAll}>
              <Selectable.Checkbox selectAll className={style.checkbox} />
              <Text as="span" variant="deci" weight="medium">
                Tout sélectionner
              </Text>
            </label>
            {displayedRows.map((row, i) => (
              <Card
                key={row.tenant.id}
                padding="sm"
                className={style.mobileCard}
              >
                <div className={style.mobileHeader}>
                  <label className={style.avatarToggle}>
                    <Selectable.Checkbox
                      value={row.tenant.id}
                      index={i}
                      className={style.hiddenCheckbox}
                    />
                    <Avatar
                      initials={row.initials}
                      size="md"
                      className={style.avatarUnchecked}
                    />
                    <span className={style.avatarChecked}>
                      <IconCheck size={22} stroke={2.5} />
                    </span>
                  </label>
                  <div className={style.tenantInfo}>
                    <div className={style.nameRow}>
                      <Link
                        to={`/bailleur/tenants/${row.tenant.id}`}
                        className={style.tenantName}
                      >
                        {row.tenant.nom}
                      </Link>
                      <RevisionTag tenant={row.tenant} />
                    </div>
                    {row.property && (
                      <Link
                        to={`/bailleur/properties/${row.property.id}`}
                        className={style.propertyLink}
                      >
                        <IconHome size={14} stroke={1.5} />
                        <Text as="span" variant="deci" color="muted">
                          {row.property.nom}
                        </Text>
                      </Link>
                    )}
                  </div>
                </div>
                <div className={style.mobileRows}>
                  <div className={style.mobileRow}>
                    <Text as="span" variant="deci" color="muted">
                      Dépôt de garantie
                    </Text>
                    <DgBadge row={row} />
                  </div>
                  <div className={style.mobileRow}>
                    <Text as="span" variant="deci" color="muted">
                      Solde
                    </Text>
                    <Text
                      as="span"
                      variant="deci"
                      weight="bold"
                      color={row.soldeColor}
                    >
                      {currencyFormatter(row.balance)}
                    </Text>
                  </div>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    </>
  );
}
